import { DeviceStatus } from '@lmc/ts-common'
import { Button, EmptyState, NoDataIcon, Tabs } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { dacConstants, dacTranslations } from 'locale/deviceAccessControl'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { HashLink } from 'react-router-hash-link'
import styled from 'styled-components'
import { DEVICES_SETTINGS_PAGE_WITH_HASH, type TableParams } from '../../types'
import { tableParamsDefaultState } from '../../utils'

type StatusSwitchProps = {
  isLoading: boolean
  hasSpecificStatusDevices: boolean
  setStatus: Dispatch<SetStateAction<DeviceStatus | undefined>>
  setTableParams: Dispatch<SetStateAction<TableParams>>
  testId?: string
}

const StatusSwitch: FC<StatusSwitchProps> = ({
  isLoading,
  hasSpecificStatusDevices,
  setStatus,
  setTableParams,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  return (
    <Tabs
      data-testid={testId}
      items={[
        {
          key: DeviceStatus.approved,
          label: t(dacTranslations.approved),
          children:
            !isLoading && !hasSpecificStatusDevices ? (
              <EmptyState
                title={t(dacConstants.NO_DEVICES_APPROVED)}
                description={
                  <EmptyStateWrapper>
                    <Text>{t(dacConstants.DEVICES_APPROVE)}</Text>
                    <HashLink to={DEVICES_SETTINGS_PAGE_WITH_HASH} smooth>
                      <StyledNavigateButton
                        variant="text"
                        data-cy="btn-preferences"
                      >
                        {t(dacConstants.PREFERENCES)}
                      </StyledNavigateButton>
                    </HashLink>
                  </EmptyStateWrapper>
                }
                icon={<NoDataIcon />}
              />
            ) : undefined
        },
        {
          key: DeviceStatus.blocked,
          label: t(dacTranslations.blocked),
          children:
            !isLoading && !hasSpecificStatusDevices ? (
              <EmptyState
                title={t(dacConstants.NO_DEVICES_BLOCKED)}
                description={
                  <EmptyStateWrapper>
                    <Text>{t(dacConstants.DEVICES_BLOCKED)}</Text>
                    <HashLink to={DEVICES_SETTINGS_PAGE_WITH_HASH} smooth>
                      <StyledNavigateButton
                        variant="text"
                        data-cy="btn-preferences"
                      >
                        {t(dacConstants.PREFERENCES)}
                      </StyledNavigateButton>
                    </HashLink>
                  </EmptyStateWrapper>
                }
                icon={<NoDataIcon />}
              />
            ) : undefined
        },
        {
          key: DeviceStatus.pending,
          label: t(dacTranslations.pending),
          children:
            !isLoading && !hasSpecificStatusDevices ? (
              <EmptyState
                title={t(dacConstants.DEVICES_PENDING)}
                icon={<NoDataIcon />}
              />
            ) : undefined
        }
      ]}
      defaultActiveKey={DeviceStatus.approved}
      onChange={(activeKey) => {
        setStatus(activeKey as unknown as DeviceStatus)
        setTableParams(tableParamsDefaultState())
      }}
    />
  )
}

const EmptyStateWrapper = styled.div`
  display: flex;
`

const StyledNavigateButton = styled(Button)`
  margin-left: 4px;
  margin-right: 4px;
`

export default StatusSwitch
